// ABOUTME: Page showing pending family invitations for a user who has no family.
// ABOUTME: Allows accepting or declining each invitation; shows empty state with create-family option.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import { Business, Person } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentAccessToken } from '../services/api';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

interface InvitationDto {
  id: number;
  familyName: string;
  invitedByName: string;
  email: string;
  role: string;
  customRole?: string;
  message?: string;
  status: string;
  invitedAt: string;
  expiresAt: string;
  token: string;
}

const PendingInvitationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [invitations, setInvitations] = useState<InvitationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const token = getCurrentAccessToken();
        const response = await fetch(`${API_BASE}/api/family/invitations/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setInvitations(data);
        } else {
          setError('Failed to load your invitations');
        }
      } catch {
        setError('Failed to load your invitations');
      } finally {
        setLoading(false);
      }
    };
    fetchInvitations();
  }, []);

  const isExpired = (invitation: InvitationDto) =>
    new Date(invitation.expiresAt) < new Date();

  const visibleInvitations = invitations.filter((inv) => !dismissed.has(inv.id));
  const allGone = visibleInvitations.length === 0;

  const handleAccept = async (invitation: InvitationDto) => {
    setActionLoading(invitation.id);
    try {
      const token = getCurrentAccessToken();
      const response = await fetch(`${API_BASE}/api/family/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ token: invitation.token }),
      });
      if (response.ok) {
        navigate('/onboarding/invited');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to accept invitation');
      }
    } catch {
      setError('Failed to accept invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (invitation: InvitationDto) => {
    setActionLoading(invitation.id);
    try {
      const token = getCurrentAccessToken();
      const response = await fetch(`${API_BASE}/api/family/invitations/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ token: invitation.token }),
      });
      if (response.ok) {
        setInvitations((prev) => prev.filter((inv) => inv.id !== invitation.id));
      } else {
        setError('Failed to decline invitation');
      }
    } catch {
      setError('Failed to decline invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = (id: number) => {
    setDismissed((prev) => new Set([...prev, id]));
  };

  const handleCreateFamily = () => navigate('/family-setup');
  const handleLogout = () => logout();

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box mt={4} mb={4}>
        <Typography variant="h4" gutterBottom textAlign="center">
          Family Invitations
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={3}>
          {invitations.length === 1
            ? "You've been invited to join a family on Let's Reset."
            : "You've been invited to join families on Let's Reset."}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {allGone ? (
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              You are not currently in a family.
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Would you like to create your own family?
            </Typography>
            <Button variant="contained" size="large" onClick={handleCreateFamily} fullWidth sx={{ mb: 1 }}>
              Create a Family
            </Button>
            <Button variant="text" color="inherit" onClick={handleLogout} fullWidth>
              Log Out
            </Button>
          </Card>
        ) : (
          <>
            {visibleInvitations.map((invitation) => (
              <Card key={invitation.id} sx={{ mb: 2 }}>
                <CardContent>
                  {isExpired(invitation) && (
                    <Alert
                      severity="warning"
                      action={
                        <Button size="small" onClick={() => handleDismiss(invitation.id)}>
                          Dismiss
                        </Button>
                      }
                      sx={{ mb: 1 }}
                    >
                      Invitation expired — contact {invitation.invitedByName} for a new one.
                    </Alert>
                  )}

                  <Box display="flex" alignItems="center" mb={1}>
                    <Business sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{invitation.familyName}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" mb={1}>
                    {/* Icon glyph sizing via fontSize, not a Typography text override */}
                    <Person sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Invited by {invitation.invitedByName}
                    </Typography>
                  </Box>

                  {invitation.customRole && (
                    <Chip label={invitation.customRole} size="small" sx={{ mb: 1 }} />
                  )}

                  {invitation.message && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                      &ldquo;{invitation.message}&rdquo;
                    </Typography>
                  )}
                </CardContent>

                {!isExpired(invitation) && (
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleAccept(invitation)}
                      disabled={actionLoading === invitation.id}
                    >
                      {actionLoading === invitation.id ? <CircularProgress size={20} /> : 'Join Family'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => handleDecline(invitation)}
                      disabled={actionLoading === invitation.id}
                    >
                      Decline
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box textAlign="center">
          <Button variant="text" color="primary" onClick={handleCreateFamily}>
            Create a new family instead
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default PendingInvitationsPage;
